import { ref } from 'vue';
import { askKnowledgeBase, askKnowledgeBaseStream } from '@/api';
import useConfig from '@/composables/useConfig';
import { SYSTEM_VARIABLES_MAP, VARIABLE_VALUE_TYPES } from '@/js/constants';
import useAnalytics from '@/composables/useAnalytics';

const INTERNAL_ERROR_PLACEHOLDER = 'internal server error';
const isKBRequestPending = ref(false);

export default function useKnowledgeBase() {
  const {
    currentConfig, setVariables, getVariable, currentLanguage, resetVariables, isPreview,
  } = useConfig();
  const { lastAnalyticData } = useAnalytics();

  function getPayload(question) {
    return {
      question,
      language: currentLanguage.value,
      threadId: getVariable(SYSTEM_VARIABLES_MAP.KB_THREAD_ID.id).value || '',
      // history: getVariable(SYSTEM_VARIABLES_MAP.KB_CONVERSATION_HISTORY.id).value || [],
    };
  }

  function updateHistory(question, answer) {
    const history = getVariable(SYSTEM_VARIABLES_MAP.KB_CONVERSATION_HISTORY.id).value;
    setVariables([
      {
        id: SYSTEM_VARIABLES_MAP.KB_CONVERSATION_HISTORY.id,
        value: { value: [...history, [question, answer]], type: VARIABLE_VALUE_TYPES.PRIMITIVE },
      },
    ]);
  }

  async function askQuestion({ question, knowledgeBaseId }) {
    isKBRequestPending.value = true;
    try {
      const { text, error, postMessage, conversationId, threadId } = await askKnowledgeBase({
        knowledgeBaseId,
        payload: getPayload(question),
        meta: {
          projectId: currentConfig.value.projectId,
          sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
          isPreview: isPreview(),
          elementId: lastAnalyticData.value.elementId,
        },
      });

      if (error) {
        return { error };
      }

      setVariables([
        { id: SYSTEM_VARIABLES_MAP.KB_RESPONSE.id, value: { value: text, type: VARIABLE_VALUE_TYPES.PRIMITIVE } },
        {
          id: SYSTEM_VARIABLES_MAP.KB_POST_ANSWER_MESSAGE_VARIABLE_ID.id,
          value: { value: postMessage, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
        },
        { id: SYSTEM_VARIABLES_MAP.KB_THREAD_ID.id, value: { value: threadId, type: VARIABLE_VALUE_TYPES.PRIMITIVE } },
      ]);
      updateHistory(question, text);

      return { text, error, postMessage, conversationId };
    } catch (err) {
      resetVariables([
        SYSTEM_VARIABLES_MAP.KB_CONVERSATION_HISTORY.id,
        SYSTEM_VARIABLES_MAP.KB_THREAD_ID.id,
      ]);
      return { error: err.response?.data?.error || INTERNAL_ERROR_PLACEHOLDER };
    } finally {
      isKBRequestPending.value = false;
    }
  }

  async function askStreamQuestion({ question, knowledgeBaseId, onMessageCallback }) {
    let fullAnswer = '';
    let postMessage = '';
    let conversationId = '';
    let threadId = '';
    let error = '';
    isKBRequestPending.value = true;
    try {
      await askKnowledgeBaseStream({
        knowledgeBaseId,
        payload: getPayload(question),
        meta: {
          projectId: currentConfig.value.projectId,
          sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
          isPreview: isPreview(),
          elementId: lastAnalyticData.value.elementId,
        },
        onMessage: (event) => {
          if (event.type === 'data') {
            fullAnswer += event.token;
          } else if (event.type === 'end') {
            postMessage = event.postMessage;
            conversationId = event.conversationId;
            threadId = event.threadId;
          } else if (event.type === 'error') {
            error = event.message;
          }

          onMessageCallback(event);
          // based on the type (start, data, end)
          // we receive here tokens (partial text) that builds up
          // the end event also contains the postMessage as well as the conversation id
        },
      });

      if (!error) {
        setVariables([
          {
            id: SYSTEM_VARIABLES_MAP.KB_RESPONSE.id,
            value: { value: fullAnswer, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
          },
          {
            id: SYSTEM_VARIABLES_MAP.KB_POST_ANSWER_MESSAGE_VARIABLE_ID.id,
            value: { value: postMessage, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
          },
          {
            id: SYSTEM_VARIABLES_MAP.KB_THREAD_ID.id,
            value: { value: threadId, type: VARIABLE_VALUE_TYPES.PRIMITIVE },
          },
        ]);
        updateHistory(question, fullAnswer);
      } else {
        resetVariables([
          SYSTEM_VARIABLES_MAP.KB_CONVERSATION_HISTORY.id,
          SYSTEM_VARIABLES_MAP.KB_THREAD_ID.id,
        ]);
      }
    } catch (err) {
      resetVariables([
        SYSTEM_VARIABLES_MAP.KB_CONVERSATION_HISTORY.id,
        SYSTEM_VARIABLES_MAP.KB_THREAD_ID.id,
      ]);
      fullAnswer = err.message;
      error = err.message;
    } finally {
      isKBRequestPending.value = false;
    }

    return {
      text: fullAnswer,
      error,
      postMessage,
      conversationId,
      threadId,
    };
  }

  return {
    isKBRequestPending,
    INTERNAL_ERROR_PLACEHOLDER,
    getPayload,
    updateHistory,
    askQuestion,
    askStreamQuestion,
  };
}
